import { FC } from 'react';

import { EVENTS, Joyride, STATUS } from 'react-joyride';

import { CoachMark } from '@/components/CoachMark';
import { TUTORIAL_BEFORE_TIMEOUT_MS, TUTORIAL_TARGET_WAIT_TIMEOUT_MS } from '@/constants';
import {
  useTutorial,
  useTutorialAutoStart,
  useTutorialBootstrap,
  useTutorialFinish,
  useTutorialMappingCounts,
  useTutorialSteps,
} from '@/hooks';

export const TutorialRunner: FC = () => {
  const {
    isActive,
    hasSeenTutorial,
    moreActionsTab,
    bulkSelectTab,
    startTutorial,
    stopTutorial,
    markAsSeen,
    setMoreActionsTab,
    setBulkSelectTab,
  } = useTutorial();

  useTutorialAutoStart({ isActive, hasSeenTutorial, startTutorial });

  const { counts, totals } = useTutorialMappingCounts(isActive);
  const { isRunning } = useTutorialBootstrap({ isActive, stopTutorial });
  const finishTour = useTutorialFinish({
    hasSeenTutorial,
    markAsSeen,
    stopTutorial,
    setMoreActionsTab,
    setBulkSelectTab,
  });
  const steps = useTutorialSteps({
    isRunning,
    counts,
    totals,
    moreActionsTab,
    setMoreActionsTab,
    bulkSelectTab,
    setBulkSelectTab,
  });

  if (!isActive || steps.length === 0) {
    return null;
  }

  return (
    <Joyride
      steps={steps}
      run={isRunning}
      continuous
      scrollToFirstStep={false}
      tooltipComponent={CoachMark}
      loaderComponent={null}
      options={{
        skipBeacon: true,
        spotlightPadding: 2,
        blockTargetInteraction: true,
        overlayClickAction: false,
        hideOverlay: false,
        disableFocusTrap: false,
        closeButtonAction: 'skip',
        targetWaitTimeout: TUTORIAL_TARGET_WAIT_TIMEOUT_MS,
        beforeTimeout: TUTORIAL_BEFORE_TIMEOUT_MS,
        overlayColor: 'rgba(25, 25, 96, 0.4)',
        zIndex: 1300,
      }}
      onEvent={(data, controls) => {
        // A target that never appears (a slow load past the bound, a 500, or a totals-vs-data
        // mismatch) skips the step and advances instead of ending the tour. Advancing is bounded by
        // the step count: past the last step Joyride emits TOUR_END. The tutorial is marked complete
        // only on a genuine end or an explicit skip, never on a missed target.
        if (data.type === EVENTS.TARGET_NOT_FOUND) {
          controls.next();
          return;
        }
        if (data.type === EVENTS.TOUR_END) {
          finishTour(data.status !== STATUS.SKIPPED);
        }
      }}
    />
  );
};
